import React, { useEffect, useState } from "react";
import { AUTHORED_MS, AnswerScene, FindScene, QualifyScene, WriteScene } from "./ProductDemoScenes";
import { useMediaQuery } from "./authFlowUi";

const TABS = [
  { n: "01", label: "Find", url: "tender-search", Scene: FindScene, cap: "Tender Search covers every active federal opportunity on SAM.gov and fit-checks each one against your company.", short: "Every SAM.gov tender, fit-checked against your company." },
  { n: "02", label: "Qualify", url: "rfp-projects/va-cloud/go-no-go", Scene: QualifyScene, cap: "Go / No-Go gives a readiness score from your past work, with the evidence behind every point.", short: "A Go / No-Go score backed by your own past work." },
  { n: "03", label: "Write", url: "rfp-copilot/proposal", Scene: WriteScene, cap: "The whole proposal gets drafted from your knowledge base: win strategy, outline and every section, with sources.", short: "Whole proposals drafted from your knowledge base, with sources." },
  { n: "04", label: "Answer", url: "rfp-projects/va-cloud/questionnaire", Scene: AnswerScene, cap: "Security and compliance questionnaires are answered automatically, then assigned to teammates to review.", short: "Questionnaires answered for you, then assigned to your team." },
];

const PANEL_GRADIENT = (mid) => `linear-gradient(160deg,#3A1449 0%,#6A2779 ${mid}%,#A04A97 100%)`;
const HEADLINE_ACCENT = {
  background: "linear-gradient(90deg,#F7B0E9,#F3904F)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

const Orb = ({ style }) => <div className="ds-orb" style={{ position: "absolute", borderRadius: "50%", pointerEvents: "none", ...style }} />;

const ProductDemoPanel = ({ compact = false, mini = false, short = false, interval = 4000 }) => {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  // t drives tab auto-advance and freezes on pause; sceneT keeps playing so a clicked tab never sits on an empty first frame.
  const [clock, setClock] = useState({ scene: 0, t: 0, sceneT: 0 });
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pageHidden, setPageHidden] = useState(document.hidden);
  const [mouse, setMouse] = useState({ x: 60, y: 30 });

  const paused = hovered || focused;
  const ticking = !pageHidden && !reducedMotion;

  useEffect(() => {
    const onVisibility = () => setPageHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (!ticking) return undefined;
    let last = performance.now();
    const id = setInterval(() => {
      const now = performance.now();
      const dt = now - last;
      last = now;
      setClock((c) => {
        const sceneT = Math.min(interval, c.sceneT + dt);
        if (paused) return { ...c, sceneT };
        if (c.t + dt >= interval) return { scene: (c.scene + 1) % TABS.length, t: 0, sceneT: 0 };
        return { scene: c.scene, t: c.t + dt, sceneT };
      });
    }, 60);
    return () => clearInterval(id);
  }, [ticking, paused, interval]);

  const { scene } = clock;
  const tab = TABS[scene];
  const sceneT = reducedMotion ? AUTHORED_MS : clock.sceneT * (AUTHORED_MS / interval);
  const progress = reducedMotion ? 100 : (clock.t / interval) * 100;
  const tilt = hovered && !reducedMotion;
  const tx = tilt ? (mouse.x - 50) / 50 : 0;
  const ty = tilt ? (mouse.y - 50) / 50 : 0;

  const pauseHandlers = {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    onFocus: () => setFocused(true),
    onBlur: (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
    },
  };

  const tabButtons = (small) =>
    TABS.map((tb, i) => {
      const on = i === scene;
      const done = i < scene;
      return (
        <button
          key={tb.label}
          type="button"
          aria-pressed={on}
          onClick={() => setClock({ scene: i, t: 0, sceneT: 0 })}
          style={{
            position: "relative",
            overflow: "hidden",
            cursor: "pointer",
            color: "#fff",
            transition: "background .2s, border-color .2s",
            ...(small
              ? { padding: "8px 4px 9px", borderRadius: 10, font: "600 12.5px Outfit, sans-serif" }
              : { textAlign: "left", padding: "10px 12px 12px", borderRadius: 12 }),
            ...(on
              ? { background: "rgba(255,255,255,.2)", border: "1px solid rgba(255,255,255,.45)" }
              : { background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.14)" }),
          }}
        >
          {small ? (
            tb.label
          ) : (
            <>
              <span style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", opacity: 0.7 }}>{tb.n}</span>
              <span style={{ display: "block", marginTop: 2, fontSize: 14.5, fontWeight: 600 }}>{tb.label}</span>
            </>
          )}
          <span style={{ position: "absolute", left: 0, bottom: 0, height: small ? 2 : 3, width: "100%", background: "rgba(255,255,255,.18)" }} />
          <span
            style={{
              position: "absolute",
              left: 0,
              bottom: 0,
              height: small ? 2 : 3,
              background: "#fff",
              width: `${on ? progress : done ? 100 : 0}%`,
              opacity: on ? 1 : done ? 0.45 : 0,
            }}
          />
        </button>
      );
    });

  if (compact) {
    return (
      <div
        {...pauseHandlers}
        style={{
          position: "relative",
          overflow: "hidden",
          borderRadius: "0 0 28px 28px",
          background: PANEL_GRADIENT(55),
          color: "#fff",
          padding: "18px 18px 44px",
          boxSizing: "border-box",
          fontFamily: "Outfit, sans-serif",
        }}
      >
        <Orb style={{ top: -90, right: -70, width: 260, height: 260, background: "radial-gradient(circle,rgba(243,144,79,.38),rgba(243,144,79,0) 70%)", filter: "blur(18px)", animation: "dsOrbC 18s ease-in-out infinite" }} />
        <Orb style={{ bottom: -110, left: -80, width: 260, height: 260, background: "radial-gradient(circle,rgba(201,94,190,.45),rgba(201,94,190,0) 70%)", filter: "blur(18px)", animation: "dsOrbA 16s ease-in-out infinite" }} />
        <img
          src="/docusensasign.svg"
          alt="DocuSensa"
          style={{ position: "relative", display: "block", width: 140, height: 26, objectFit: "contain", objectPosition: "left", filter: "brightness(0) invert(1)" }}
        />
        {!mini && (
          <h2 style={{ position: "relative", margin: "14px 0 0", fontSize: 21, lineHeight: 1.18, fontWeight: 600, letterSpacing: "-.015em" }}>
            Find the right tenders. <span style={HEADLINE_ACCENT}>Win them faster.</span>
          </h2>
        )}
        <div style={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 6, marginTop: 12 }}>
          {tabButtons(true)}
        </div>
        <p key={scene} style={{ position: "relative", margin: "9px 0 0", minHeight: 36, fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: "rgba(255,255,255,.92)", animation: "dsRiseSm 300ms both" }}>
          {tab.short}
        </p>
      </div>
    );
  }

  const { Scene } = tab;

  return (
    <div
      {...pauseHandlers}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setMouse({ x: Math.round(((e.clientX - r.left) / r.width) * 100), y: Math.round(((e.clientY - r.top) / r.height) * 100) });
      }}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: 28,
        overflow: "hidden",
        background: PANEL_GRADIENT(45),
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        padding: short ? "30px 36px 26px" : "40px 44px 34px",
        boxSizing: "border-box",
        fontFamily: "Outfit, sans-serif",
      }}
    >
      <Orb style={{ top: -120, right: -120, width: 440, height: 440, background: "radial-gradient(circle,rgba(243,144,79,.30),rgba(243,144,79,0) 70%)", filter: "blur(24px)", animation: "dsOrbC 22s ease-in-out infinite" }} />
      <Orb style={{ bottom: -160, left: -100, width: 460, height: 460, background: "radial-gradient(circle,rgba(201,94,190,.45),rgba(201,94,190,0) 70%)", filter: "blur(26px)", animation: "dsOrbA 17s ease-in-out infinite" }} />
      {!reducedMotion && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            transition: "opacity .3s",
            opacity: hovered ? 1 : 0,
            background: `radial-gradient(460px circle at ${mouse.x}% ${mouse.y}%,rgba(255,255,255,.16),transparent 60%)`,
          }}
        />
      )}

      <div style={{ position: "relative" }}>
        <p style={{ margin: 0, fontSize: 12, fontWeight: 600, letterSpacing: ".16em", textTransform: "uppercase", color: "rgba(255,255,255,.65)" }}>
          From tender to submitted bid
        </p>
        <h2 style={{ margin: "10px 0 0", fontSize: short ? 30 : 34, lineHeight: 1.12, fontWeight: 600, letterSpacing: "-.02em", textWrap: "balance" }}>
          Find the right tenders. <span style={HEADLINE_ACCENT}>Win them faster.</span>
        </h2>
      </div>

      <div style={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 8, marginTop: short ? 18 : 24 }}>
        {tabButtons(false)}
      </div>

      <div style={{ position: "relative", marginTop: 18, flex: 1, minHeight: 0, perspective: 1200 }}>
        <div
          style={{
            height: "100%",
            borderRadius: 18,
            background: "#fff",
            overflow: "hidden",
            boxShadow: "0 40px 80px -36px rgba(20,4,30,.8),0 0 0 1px rgba(255,255,255,.4)",
            transition: "transform .25s ease-out",
            transform: `rotateY(${(tx * 4).toFixed(2)}deg) rotateX(${(-ty * 3).toFixed(2)}deg)`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 7, height: 38, padding: "0 14px", borderBottom: "1px solid #F0E9F2", background: "#FBF8FC" }}>
            {["#F2B8C6", "#F4D9A6", "#BFE3C8"].map((c) => (
              <span key={c} style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
            ))}
            <span style={{ marginLeft: 10, padding: "4px 12px", borderRadius: 8, background: "#fff", border: "1px solid #EFE7F1", font: "500 11.5px ui-monospace,Menlo,monospace", color: "#8B7D91", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              app.docusensa.ai/{tab.url}
            </span>
            <span style={{ marginLeft: "auto", flex: "none", display: "flex", alignItems: "center", gap: 6, font: "600 11px Outfit, sans-serif", color: paused ? "#A04A97" : "#1F9A6B" }}>
              {paused ? "❚❚ Paused" : "● Live demo"}
            </span>
          </div>
          <div style={{ padding: "20px 22px", color: "#18101C" }}>
            <Scene key={scene} t={sceneT} />
          </div>
        </div>
      </div>

      <div style={{ position: "relative", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, marginTop: 18 }}>
        <p style={{ margin: 0, maxWidth: 440, minHeight: 44, fontSize: 15, fontWeight: 500, lineHeight: 1.45, color: "rgba(255,255,255,.92)", textWrap: "pretty" }}>
          {tab.cap}
        </p>
        <span style={{ flex: "none", fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,.55)" }}>{paused ? "Paused" : "Hover to pause"}</span>
      </div>
    </div>
  );
};

export default ProductDemoPanel;
